<template>
  <v-dialog v-model="viewDialog" class="the-app-dialogs" persistent max-width="1000px">
    <v-card>
      <v-layout row class="no-select not-printable" style="background-color:#09132f">
        <h3 id="dialog-title" class="text-uppercase px-3 pt-3" style="text-xs-center letter-spacing:2px">
          <v-layout align-center>
            <v-icon light x-large class="rgba(0,0,0,0.3)--text pb-1">fa-dolly</v-icon>
            <span class="px-3">{{ $t('View Mixture Information') }}</span>
          </v-layout>
        </h3>
        <v-spacer />
        <v-card-actions>
          <v-btn fab icon dark @click="$emit('hidemodal')">
            <v-icon>close</v-icon>
          </v-btn>
        </v-card-actions>
      </v-layout>

      <v-card-title class="view-dialog-content grey lighten-3" id="printArea">
        <invoice-header class="only-for-print" />

        <v-layout column>
          <table class="section-title-table only-for-print">
            <tr>
              <th class="section-title-content">{{ $t('Mixture') }}</th>
            </tr>
          </table>

          <v-layout row wrap pa-2 class="invoice-content">
            <v-flex xs4 class="pb-3 info-table">
              <div class="caption no-select">{{ $t('Name') }}</div>
              <strong>{{ mixtureInfo.mixture_name || '-' }}</strong>
            </v-flex>

            <v-flex xs4 class="pb-3 info-table">
              <div class="caption no-select">{{ $t('Product Weight') }}</div>
              <strong>{{ mixtureInfo.product_weight || '-' }}</strong>
            </v-flex>

            <v-flex xs4 class="pb-3 info-table">
              <div class="caption no-select">
                {{ $t('Production Quantity') }}
              </div>
              <strong>{{ mixtureInfo.estimated_product_quantity || '-' }}</strong>
            </v-flex>

            <table class="printed-table" cellspacing="0">
              <thead>
                <tr class="only-for-print">
                  <td>
                    <div class="header-space">&nbsp;</div>
                  </td>
                </tr>
                <tr>
                  <th xs1 class="pa-2 view-table-header">#</th>
                  <th class="view-table-header" xs5 pa-2>
                    {{ $t('Raw Material') }}
                  </th>
                  <th class="view-table-header" xs2 pa-2>
                    {{ $t('Quantity') }}
                  </th>
                  <th class="view-table-header" xs2 pa-2>
                    {{ `${$t('Price')} (${systemCurrency})` }}
                  </th>
                  <th class="view-table-header" xs2 pa-2>
                    {{ `${$t('Total')} (${systemCurrency})` }}
                  </th>
                </tr>
              </thead>
              <template v-if="mixtureInfo.raw_materials && mixtureInfo.raw_materials.length > 0">
                <tbody>
                  <tr v-for="(row, index) in mixtureInfo.raw_materials" :key="index">
                    <td class="view-table-row">
                      <p class="view-table-content">{{ index + 1 }}</p>
                    </td>
                    <td class="view-table-row">
                      <p class="view-table-content">
                        {{ row.raw_material_name }}
                      </p>
                    </td>
                    <td class="view-table-row">
                      <p class="view-table-content">
                        {{ row.mixture_quantity | currency }}
                      </p>
                    </td>
                    <td class="view-table-row">
                      <p class="view-table-content">
                        {{ row.product_average_price | currency }}
                      </p>
                    </td>
                    <td class="view-table-row">
                      <p class="view-table-content">
                        {{ row.cost | currency }}
                      </p>
                    </td>
                  </tr>
                  <tr>
                    <td colspan="4" class="no-select last-table-row">
                      <strong>{{ `${$t('Total Cost')} (${systemCurrency})` }}</strong>
                    </td>
                    <td class="last-table-row2">
                      <strong v-if="mixtureInfo.mixture_total_cost">{{
                        mixtureInfo.mixture_total_cost | currency
                      }}</strong>
                      <strong v-else>-</strong>
                    </td>
                  </tr>
                </tbody>
              </template>
              <tfoot>
                <tr class="only-for-print">
                  <td>
                    <div class="footer-space">&nbsp;</div>
                  </td>
                </tr>
              </tfoot>
            </table>

            <invoice-footer
              :showSignatureSection="false"
              :accountantName="''"
              :payerRecipientName="''"
              :payerOrRecipient="''"
              class="only-for-print"
            />
          </v-layout>
        </v-layout>
      </v-card-title>
      <v-divider light />

      <v-card-actions class="grey lighten-3 not-printable">
        <v-layout justify-end>
          <v-btn outline color="primary" @click="$emit('hidemodal')">{{ $t('Close') }}</v-btn>
          <v-btn color="primary" @click="printMe()">
            <v-icon :right="$vuetify.rtl" :left="!$vuetify.rtl">print</v-icon>
            {{ $t('Print') }}
          </v-btn>
        </v-layout>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters } from 'vuex'
import * as helpers from '@/mixins/helperFunctions.js'
import ignored from 'print-this'

export default {
  name: 'view-component',

  components: {
    invoiceHeader: () => import('@/views/_partials/invoiceHeader.vue'),
    invoiceFooter: () => import('@/views/_partials/invoiceFooter.vue')
  },

  props: {
    viewDialog: { type: Boolean, required: true }
  },

  data() {
    return {
      systemCurrency: localStorage.systemCurrency,
      dialogs: {
        imageViewerDialog: false
      }
    }
  },

  computed: {
    ...mapGetters({
      baseUrls: 'misc/getBaseUrls',
      mixtureInfo: 'mixtures/getMixtureInfo'
    })
  },

  updated() {
    document.addEventListener('keyup', e => {
      // if the "ESC" button was pressed and this dialog is shown, hide it
      if (e.keyCode === 27 && this.viewDialog) {
        this.$emit('hidemodal')
      }
    })
  },

  methods: {
    lnToBr(str) {
      return helpers.newLineToBreakLine(str)
    },

    printMe() {
      let onlyForPrint = document.querySelectorAll('.only-for-print')
      let notPrintable = document.querySelectorAll('.not-printable')
      let invoiceContent = document.querySelector('.invoice-content')
      let theDialogContent = document.querySelector('.view-dialog-content')
      let infoTable = document.querySelector('.info-table')
      theDialogContent.classList.remove('grey')
      infoTable.classList.add('add-padding-for-info-table')
      invoiceContent.style.marginTop = '90px'
      notPrintable.forEach(element => {
        element.style.display = 'none'
      })
      onlyForPrint.forEach(element => {
        element.style.display = 'block'
      })
      document.querySelector('.printed-table').style.marginTop = '-120px'

      this.$htmlToPaper('printArea', () => {
        infoTable.classList.remove('add-padding-for-info-table')
        theDialogContent.classList.add('grey')
        invoiceContent.style.marginTop = '0px'
        onlyForPrint.forEach(element => {
          element.style.display = 'none'
        })
        notPrintable.forEach(element => {
          element.style.display = 'flex'
        })
        document.querySelector('.printed-table').style.marginTop = '0px'
      })
    }
  }
}
</script>

<style scoped>
.only-for-print {
  display: none;
}
</style>
